// REL_1.1.15 F-262 §3 + Corrigendum 2 §1.2 — Country settings context.
// Per Corrigendum 2 (user-direct 2026-05-27 ~15:30 PT): country is HARDCODED "US" for the
// current phase. CF-IPCountry / GeoIP / Front Door rule deferred to REL_1.3+.
//
// This hook exposes the spec-§1.2-canonical pattern:
//
//   const { country, setOverride, isOverride } = useCountrySettings();
//
// `country` resolves to override-if-set else detected-default ("US"). Override path stays
// open so REL_1.3+ reactivation only needs to swap lib/country.ts detectCountry().
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  detectCountry,
  SUPPORTED_COUNTRIES,
  type CountryCode,
} from "@/lib/country";

const STORAGE_KEY = "deedo-marketing-country-override";

interface CountrySettings {
  /** Resolved country = override if set, else detected default. */
  country: CountryCode;
  /** Build-time-detected country (per Corrigendum 2 = "US"; ignores override). */
  detectedCountry: CountryCode;
  /** Set or clear the user override. Pass null to revert to detected default. */
  setOverride: (c: CountryCode | null) => void;
  isOverride: boolean;
}

const CountryContext = createContext<CountrySettings | null>(null);

function readStoredOverride(): CountryCode | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    if (SUPPORTED_COUNTRIES.some((c) => c.code === raw)) return raw as CountryCode;
  } catch {
    /* localStorage unavailable — ignore */
  }
  return null;
}

export function CountrySettingsProvider({ children }: { children: ReactNode }) {
  const [override, setOverride] = useState<CountryCode | null>(() => readStoredOverride());

  useEffect(() => {
    if (override === null) {
      try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    } else {
      try { window.localStorage.setItem(STORAGE_KEY, override); } catch { /* ignore */ }
    }
  }, [override]);

  const detectedCountry = detectCountry();
  const value: CountrySettings = {
    country: override ?? detectedCountry,
    detectedCountry,
    setOverride,
    isOverride: override !== null,
  };

  return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>;
}

export function useCountrySettings(): CountrySettings {
  const ctx = useContext(CountryContext);
  if (!ctx) {
    throw new Error("useCountrySettings must be used within CountrySettingsProvider");
  }
  return ctx;
}

/** Convenience read of the detected (non-overridden) country. Per spec §1.2 canonical name. */
export function useDetectedCountry(): CountryCode {
  return detectCountry();
}
